import { v4 as uuid } from 'uuid'
import { getXCLEARCorrelationInfo } from '@api/utils'
import { DiscreteEvent, dispatchCustomEvent, KPI } from '@utils/customEvents'
import type { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import { AuthPipeMeta, PREFIX } from './types'

export class AuthFlowSession {
  redirectCount = 0
  redirectHistory: string[] = []
  authFlowSessionId = uuid()
  lastEvent = ''
  concluded = false
  meta: AuthPipeMeta | undefined = undefined
  debug = false

  get sessionId() {
    return this.authFlowSessionId
  }

  get state() {
    return {
      redirectCount: this.redirectCount,
      redirectHistory: this.redirectHistory,
      authFlowSessionId: this.authFlowSessionId,
      lastEvent: this.lastEvent,
      concluded: this.concluded,
      meta: this.meta,
      debug: this.debug,
    }
  }

  trackRedirect(path: string): number {
    this.redirectCount++
    this.redirectHistory.push(path)
    if (this.redirectHistory.length > 10) this.redirectHistory.shift()
    return this.redirectCount
  }

  trackEvent(label: string) {
    this.lastEvent = label
  }

  resetRedirects() {
    this.redirectCount = 0
    this.redirectHistory = []
  }

  resetSession() {
    if (!this.concluded && this.lastEvent) {
      const xCLEARCorrelationInfo =
        this.meta?.xCLEARCorrelationInfo || getXCLEARCorrelationInfo()
      const payload = {
        payload: { message: PREFIX + 'Flow abandoned' },
        metadata: {
          xCLEARCorrelationInfo,
          authFlowSessionId: this.authFlowSessionId,
          flow: this.meta?.flow ?? 'unknown',
          route: this.meta?.route ?? '',
          kpi: KPI.USER_INTERACTION,
          lastEvent: this.lastEvent,
          type: 'ABANDONED',
          reason: `Flow abandoned at: ${this.lastEvent}`,
          event: DiscreteEvent.CLIENT_SIDE_ERROR,
        },
      }
      if (this.debug) console.error('[AUTH:ABANDONED]', payload)
      dispatchCustomEvent(
        'ERROR' as Enrollment.LogEntryLevelEnum.ERROR,
        payload
      )
    }
    this.redirectCount = 0
    this.redirectHistory = []
    this.authFlowSessionId = uuid()
    this.lastEvent = ''
    this.concluded = false
    this.meta = null as never
  }

  setDebug(enabled: boolean) {
    this.debug = enabled
  }

  __reset() {
    this.redirectCount = 0
    this.redirectHistory = []
    this.authFlowSessionId = 'test-session-id'
    this.lastEvent = ''
    this.concluded = false
    this.meta = null as never
  }
}

export const session = new AuthFlowSession()
